import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Pagination,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { notificationLogsService } from './notificationLogs.service';
import type { NotificationLog } from './notificationLogs.dto';
import { formatDateTime } from '@/helpers';

const itemsPerPage = 10;

const NotificationLogs = () => {
  const { token, logout } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [selectedLog, setSelectedLog] = useState<NotificationLog | null>(null);

  useEffect(() => {
    notificationLogsService.setLogoutCallback(logout);
  }, [logout]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const response = await notificationLogsService.getNotificationLogs(
        token,
        {
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearchTerm || undefined,
        }
      );
      setLogs(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalLogs(response.meta?.total || 0);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch notification logs'
      );
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showDetails = (log: NotificationLog) => {
    setSelectedLog(log);
  };

  const closeDetails = () => setSelectedLog(null);

  const statusColor = (status: NotificationLog['status']) =>
    status === 'SENT' ? 'success' : 'error';

  const chipColor = (value?: string) =>
    value === 'SMS' || value === 'FAILED' ? 'warning' : 'default';

  const truncate = (text?: string, len = 60) => {
    if (!text) return '';
    return text.length > len ? `${text.slice(0, len)}…` : text;
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4">Notification Logs</Typography>
          <Typography variant="body2" color="text.secondary">
            View delivery history for email notifications.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by contact, booking ID, or response text..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Booking ID</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Response</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading notification logs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No notification logs match your search.'
                        : 'No notification logs found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.bookingId ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.contact}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.notificationType.replace(/_/g, ' ')}
                        size="small"
                        color="default"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.channel}
                        size="small"
                        color={log.channel === 'SMS' ? 'info' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.hours ? `${log.hours} (Hours)` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.status}
                        size="small"
                        color={statusColor(log.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(log.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          {truncate(log.response)}
                        </Typography>
                        {log.response && log.response.length > 60 && (
                          <IconButton
                            size="small"
                            onClick={() => showDetails(log)}
                            title="View full response"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!loading && totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
        >
          <Typography variant="body2" color="text.secondary">
            Showing page {currentPage} of {totalPages} • {totalLogs} total
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={!!selectedLog}
        onClose={closeDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Notification Response</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Contact: {selectedLog?.contact} • Type:{' '}
            {selectedLog?.notificationType} • Channel: {selectedLog?.channel}
          </Typography>
          <Typography whiteSpace="pre-wrap">
            {selectedLog?.response || 'No response available.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationLogs;
